import { FunctionComponent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { faPencil } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

interface InlineNameProps {
	value: string;
	onChange: (value: string) => void;
	className?: string;
	testId?: string;
	/** Bump to open the editor from outside (a "Rename" button). */
	editSignal?: number;
}

/**
 * A name that edits in place (PREC-4055): a pencil on hover, a click or double
 * click opens the input, Enter commits, Escape restores. An empty name is not
 * committed — the rows the API stores must keep a name.
 *
 * The pencil is positioned absolutely and inherits the row's background
 * (PREC-4079): in flow it added its width to the cell on hover, and because a
 * table column is sized from its content that widened the whole first column
 * every time the pointer crossed a row. Out of flow it costs no width and, when
 * the name already fills the cell, paints over its last characters instead.
 */
export const InlineName: FunctionComponent<InlineNameProps> = ({
	value,
	onChange,
	className,
	testId,
	editSignal = 0,
}) => {
	const { t } = useTranslation();
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(value);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (editing) {
			inputRef.current?.focus();
			inputRef.current?.select();
		}
	}, [editing]);

	useEffect(() => {
		if (editSignal > 0) {
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setDraft(value);
			setEditing(true);
		}
		// The signal is the trigger; the value it opens with is whatever is current then.
	}, [editSignal]);

	const commit = () => {
		const trimmed = draft.trim();

		if (trimmed && trimmed !== value) {
			onChange(trimmed);
		}

		setEditing(false);
	};

	const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault();
			commit();
		}

		if (event.key === "Escape") {
			setDraft(value);
			setEditing(false);
		}
	};

	if (editing) {
		return (
			<input
				ref={inputRef}
				className={classNames(
					"input-styles h-6 w-full rounded-md border px-1.5 text-xs",
					className
				)}
				data-testid={testId ? `${testId}-input` : undefined}
				value={draft}
				onBlur={commit}
				onChange={(event) => setDraft(event.target.value)}
				onKeyDown={onKeyDown}
			/>
		);
	}

	return (
		<span className={classNames("group relative flex min-w-0 items-center bg-inherit", className)}>
			<span
				className="min-w-0 cursor-text truncate"
				data-testid={testId}
				onDoubleClick={() => {
					setDraft(value);
					setEditing(true);
				}}
			>
				{value || t("leadScoring.shell.untitled")}
			</span>
			<button
				aria-label={t("leadScoring.editName")}
				className="absolute inset-y-0 right-0 hidden cursor-pointer items-center bg-inherit pl-1.5 text-text-disabled hover:text-blue-action group-hover:flex"
				data-testid={testId ? `${testId}-edit` : undefined}
				type="button"
				onClick={() => {
					setDraft(value);
					setEditing(true);
				}}
			>
				<Icon className="h-3 w-3" icon={faPencil} />
			</button>
		</span>
	);
};
